/**
 * The Normal Map: 333x333x333 Poetry Collection
 * All 126 poems across 14 chapters
 * Each poem: 3 stanzas × 3 lines × 3 words = 27 words
 */

// Chapter metadata with colors and themes
export const chapters = [
  { id: 1, title: 'The Multidimensional Human', theme: '3D/4D/5D dimensional model', color: '#D4AF37' },
  { id: 2, title: 'The Language of Coherence', theme: 'Communication, resonance between beings', color: '#4169E1' },
  { id: 3, title: 'Sacred Geometry', theme: 'Flower of Life, Platonic solids', color: '#9B30FF' },
  { id: 4, title: 'Plasma Consciousness', theme: 'Fourth state of matter, aurora', color: '#00CED1' },
  { id: 5, title: 'The Hermetic Principles', theme: 'As above so below, mentalism', color: '#FFD700' },
  { id: 6, title: 'Movement Medicine', theme: 'Body, joints, movement, dance', color: '#FF6347' },
  { id: 7, title: 'Silent Vibrations', theme: 'Sound, cymatics, frequency', color: '#1E90FF' },
  { id: 8, title: 'Breath as Bridge', theme: 'Respiration, expansion/contraction', color: '#98FB98' },
  { id: 9, title: 'Nervous System Coherence', theme: 'Vagus nerve, neural network', color: '#FF69B4' },
  { id: 10, title: 'Love as Root Frequency', theme: 'Heart coherence, electromagnetic field', color: '#FF1493' },
  { id: 11, title: 'Collective Consciousness', theme: 'Morphogenetic fields, connection', color: '#00FA9A' },
  { id: 12, title: 'Transcendence', theme: 'Dark night to dawn, spiral ascent', color: '#9370DB' },
  { id: 13, title: 'The Normal Map', theme: 'Integration, fractal wholeness', color: '#FFA500' },
  { id: 14, title: 'Practical Protocols', theme: '40-day journey, daily practice', color: '#8B4513' }
]

// Audio track mapping for each chapter (clean filenames + display names)
export const audioTracks = {
  1:  { file: 'ch01-ufltit.mp3',                    display: 'ufltit' },
  2:  { file: 'ch02-i-love-your-echoes.mp3',         display: 'I Love Your Echoes' },
  3:  { file: 'ch03-helix-cipher.mp3',               display: 'Helix Cipher' },
  4:  { file: 'ch04-portal-opening.mp3',             display: 'Portal Opening' },
  5:  { file: 'ch05-asc-spiral.mp3',                 display: 'ASC Spiral' },
  6:  { file: 'ch06-flow.mp3',                       display: 'Flow' },
  7:  { file: 'ch07-mrflng.mp3',                     display: 'mrflng' },
  8:  { file: 'ch08-lusher-looser-let-go.mp3',       display: 'Lusher Looser, Let Go' },
  9:  { file: 'ch09-cataclysmic-coil.mp3',           display: 'Cataclysmic Coil' },
  10: { file: 'ch10-love-is-still-the-loudest.mp3',  display: 'Love Is Still the Loudest Thing Ever Made' },
  11: { file: 'ch11-you-reverberate.mp3',            display: 'You Reverberate' },
  12: { file: 'ch12-absolute-self-consistency.mp3',   display: 'Absolute Self-Consistency' },
  13: { file: 'ch13-notes-to-a-shattered-self.mp3',  display: 'Notes to a Simply Shattered Self' },
  14: { file: 'ch14-velvet-horizons.mp3',            display: 'Velvet Horizons' },
}

// All 126 poems organized by chapter
export const poems = {
  1: [
    {
      id: '1.1',
      title: 'The Invitation',
      stanzas: [
        ['Body knows before', 'mind catches the', 'whisper of becoming'],
        ['We are three', 'dimensions dancing through', 'one singular moment'],
        ['What if you', 'already contain everything', 'you seek outside']
      ]
    },
    {
      id: '1.2',
      title: 'The Uncertainty',
      stanzas: [
        ['Sarah felt something', 'her instruments couldn\'t', 'name or measure'],
        ['Between two nervous', 'systems a warmth', 'passed without permission'],
        ['The framework cracked', 'and through that', 'gap light entered']
      ]
    },
    {
      id: '1.3',
      title: 'The First Step',
      stanzas: [
        ['Put down certainty', 'like a heavy', 'coat no longer'],
        ['Stand at threshold', 'where science meets', 'what traditions whispered'],
        ['Your body remembers', 'what your mind', 'forgot it knew']
      ]
    },
    {
      id: '1.4',
      title: 'Going Deeper',
      stanzas: [
        ['Three layers nested', 'mind field soul', 'each containing another'],
        ['Physical holds emotional', 'emotional holds eternal', 'nothing stands alone'],
        ['Descend into what', 'you thought was', 'only flesh bone']
      ]
    },
    {
      id: '1.5',
      title: 'The Resistance',
      stanzas: [
        ['The zero multiplies', 'everything to nothing', 'when one dimension'],
        ['carries old wounds', 'blocking the signal', 'that was always'],
        ['broadcasting from your', 'depth but landing', 'scrambled by fear']
      ]
    },
    {
      id: '1.6',
      title: 'The Breakthrough',
      stanzas: [
        ['Someone placed hands', 'on my shoulders', 'stayed without fixing'],
        ['The shaking came', 'then tears then', 'something finally released'],
        ['Not worthy yet', 'I felt empty', 'but finally clean']
      ]
    },
    {
      id: '1.7',
      title: 'New Understanding',
      stanzas: [
        ['Five D broadcasts', 'always and forever', 'love without condition'],
        ['Four D filters', 'translates and sometimes', 'distorts the message'],
        ['Three D receives', 'whatever gets through', 'the emotional static']
      ]
    },
    {
      id: '1.8',
      title: 'Embodied Wisdom',
      stanzas: [
        ['Softening of belly', 'breath moving lower', 'time becoming elastic'],
        ['Warmth in chest', 'boundaries dissolving gently', 'stillness that lives'],
        ['You never left', 'anywhere you just', 'stopped receiving clearly']
      ]
    },
    {
      id: '1.9',
      title: 'The Gift',
      stanzas: [
        ['Here is map', 'not territory itself', 'but direction toward'],
        ['You are already', 'multidimensional being having', 'always been whole'],
        ['The signal continues', 'your work clears', 'the receiving channel']
      ]
    }
  ],
  2: [
    {
      id: '2.1',
      title: 'The Space Between',
      stanzas: [
        ['They argued hours', 'same words recycled', 'missing each other'],
        ['Then Eliana stopped', 'exhaled and looked', 'really looked deeper'],
        ['Something shifted there', 'in the silence', 'that wasn\'t words']
      ]
    },
    {
      id: '2.2',
      title: 'The Longing',
      stanzas: [
        ['We speak at', 'each other daily', 'rarely truly meeting'],
        ['Heart fields extend', 'beyond our skin', 'seeking another heart'],
        ['Can I reach', 'you or just', 'the walls between']
      ]
    },
    {
      id: '2.3',
      title: 'Opening the Door',
      stanzas: [
        ['Love is not', 'emotion but carrier', 'wave for truth'],
        ['It makes boundaries', 'transparent not dissolved', 'two becoming meeting'],
        ['Start here breathing', 'together before any', 'words are spoken']
      ]
    },
    {
      id: '2.4',
      title: 'Expression Rising',
      stanzas: [
        ['Logos speaks through', 'body voice gesture', 'presence becoming form'],
        ['Not just content', 'but tone rhythm', 'the space around'],
        ['What wants to', 'be said through', 'you right now']
      ]
    },
    {
      id: '2.5',
      title: 'Learning Reception',
      stanzas: [
        ['I planned response', 'while she spoke', 'technically there present'],
        ['My door unlocked', 'but not swinging', 'open when knocked'],
        ['Being responsive means', 'tuning in actually', 'receiving not calculating']
      ]
    },
    {
      id: '2.6',
      title: 'Resonance Arrives',
      stanzas: [
        ['Something new emerged', 'neither brought it', 'both created space'],
        ['The third thing', 'born in meeting', 'surprised us both'],
        ['This is gnosis', 'direct knowing through', 'presence not concept']
      ]
    },
    {
      id: '2.7',
      title: 'The Triad Complete',
      stanzas: [
        ['Expression puts truth', 'into form transmitted', 'logos made manifest'],
        ['Reception creates space', 'eros drawing forth', 'welcoming what comes'],
        ['Resonance emerges when', 'both are coherent', 'gnosis between nodes']
      ]
    },
    {
      id: '2.8',
      title: 'I and Thou',
      stanzas: [
        ['Not talking at', 'objects to use', 'but meeting presence'],
        ['Both transformed by', 'encounter that neither', 'controlled or forced'],
        ['All real living', 'is meeting said', 'Buber and meant']
      ]
    },
    {
      id: '2.9',
      title: 'Sacred Ground',
      stanzas: [
        ['The space between', 'us births something', 'new we carry'],
        ['Language becomes practice', 'spiritual not technique', 'participating in reality'],
        ['Every conversation potentially', 'sacred when presence', 'meets presence fully']
      ]
    }
  ],
  3: [
    {
      id: '3.1',
      title: 'The Pattern Calls',
      stanzas: [
        ['Chartres Cathedral rose', 'windows catching light', 'in geometric arrays'],
        ['Why does this', 'feel like coming', 'home to something'],
        ['The builders discovered', 'what was already', 'written into reality']
      ]
    },
    {
      id: '3.2',
      title: 'Seeing Through',
      stanzas: [
        ['Honeycomb cells hexagons', 'optimal for storing', 'maximum with minimum'],
        ['No bee decided', 'no committee voted', 'nature solves itself'],
        ['What mathematics already', 'knows before we', 'discover it exists']
      ]
    },
    {
      id: '3.3',
      title: 'The First Circle',
      stanzas: [
        ['Begin with point', 'having position but', 'no dimension yet'],
        ['The point moves', 'inscribes a circle', 'center meets circumference'],
        ['From unity duality', 'from one comes', 'two and three']
      ]
    },
    {
      id: '3.4',
      title: 'The Vesica Opens',
      stanzas: [
        ['Two circles overlap', 'creating almond shape', 'portal of creation'],
        ['Where wholes meet', 'something new emerges', 'neither contained alone'],
        ['This is relationship', 'geometrized the shape', 'of authentic exchange']
      ]
    },
    {
      id: '3.5',
      title: 'The Flower Blooms',
      stanzas: [
        ['Nineteen circles interlocking', 'flower of life', 'pattern repeating everywhere'],
        ['Egypt to China', 'no historical contact', 'same discovery made'],
        ['The template beneath', 'all form visible', 'coherence made pattern']
      ]
    },
    {
      id: '3.6',
      title: 'Golden Unfolding',
      stanzas: [
        ['One point six', 'one eight recurring', 'phi in everything'],
        ['Nautilus shell spirals', 'sunflower seed arrangements', 'galaxy arm curves'],
        ['Not arbitrary beauty', 'but optimal efficiency', 'of growth itself']
      ]
    },
    {
      id: '3.7',
      title: 'Solids and Souls',
      stanzas: [
        ['Five shapes only', 'perfectly regular forms', 'tetrahedron cube octahedron'],
        ['Icosahedron dodecahedron nest', 'within each other', 'one geometric system'],
        ['Elements in body', 'fire earth air', 'water ether mapped']
      ]
    },
    {
      id: '3.8',
      title: 'Sound Made Visible',
      stanzas: [
        ['Vibrate a plate', 'sand organizes itself', 'into perfect pattern'],
        ['This is cymatics', 'proof that frequency', 'creates geometric form'],
        ['Your body water', 'cells fluid responding', 'to emotional vibration']
      ]
    },
    {
      id: '3.9',
      title: 'Doorway Not Destination',
      stanzas: [
        ['The pattern reveals', 'not contains coherence', 'map toward territory'],
        ['Draw impossible triangle', 'participate without understanding', 'trust before knowing'],
        ['Geometry is language', 'consciousness speaking through', 'form to form']
      ]
    }
  ],
  4: [
    {
      id: '4.1',
      title: 'The Fourth State',
      stanzas: [
        ['Ninety-nine percent', 'of visible universe', 'is plasma state'],
        ['Not solid liquid', 'gas but something', 'responsive and alive'],
        ['We live in', 'plasma cosmos without', 'knowing its name']
      ]
    },
    {
      id: '4.2',
      title: 'What the Ancients Saw',
      stanzas: [
        ['When yogis spoke', 'of prana chi', 'aura subtle light'],
        ['Were they describing', 'something science now', 'learns to measure'],
        ['The field around', 'you carrying emotion', 'information and potential']
      ]
    },
    {
      id: '4.3',
      title: 'Honest Uncertainty',
      stanzas: [
        ['I cannot prove', 'consciousness operates like', 'plasma in laboratory'],
        ['But when I', 'started thinking field', 'responsive to intention'],
        ['My practice deepened', 'whether model true', 'or useful metaphor']
      ]
    },
    {
      id: '4.4',
      title: 'Three Lights',
      stanzas: [
        ['Photonic light physical', 'electromagnetic measurable seen', 'with your eyes'],
        ['Antiphotonic light dreams', 'emotional symbolic meaning', 'felt not measured'],
        ['Omniphotonic unified field', 'experienced as love', 'containing all frequencies']
      ]
    },
    {
      id: '4.5',
      title: 'The Multiplier',
      stanzas: [
        ['Mind times field', 'times soul equals', 'coherence power available'],
        ['Not addition but', 'multiplication meaning zero', 'anywhere zeros everything'],
        ['Your affirmations fail', 'when four D', 'carries unprocessed shame']
      ]
    },
    {
      id: '4.6',
      title: 'Clearing the Channel',
      stanzas: [
        ['Soul always broadcasts', 'wholeness love guidance', 'signal never stops'],
        ['Mind can tune', 'to any station', 'beliefs are settings'],
        ['Four D determines', 'what gets through', 'clear the interference']
      ]
    },
    {
      id: '4.7',
      title: 'Field Coherence',
      stanzas: [
        ['Three D grounded', 'weight in lower', 'body feet connected'],
        ['Four D open', 'flowing intuition accessible', 'heart area expanded'],
        ['Five D spacious', 'timeless love without', 'object just presence']
      ]
    },
    {
      id: '4.8',
      title: 'Heart Center',
      stanzas: [
        ['Heart generates field', 'hundred times stronger', 'than brain electrically'],
        ['When appreciation flows', 'rhythm becomes coherent', 'others respond unconsciously'],
        ['You are tuning', 'fork walking through', 'world striking resonance']
      ]
    },
    {
      id: '4.9',
      title: 'Nested Scales',
      stanzas: [
        ['Individual field yours', 'relational field between', 'collective field humanity'],
        ['Change your coherence', 'shift the whole', 'nodes in web'],
        ['The practice matters', 'beyond personal benefit', 'contributing to all']
      ]
    }
  ],
  5: [
    {
      id: '5.1',
      title: 'The Operating System',
      stanzas: [
        ['Elena watched mother', 'with terminal cancer', 'begin strange practice'],
        ['Breathing geometric patterns', 'murmuring as above', 'so below daily'],
        ['Six weeks passed', 'then twelve then', 'remission unexplained stabilization']
      ]
    },
    {
      id: '5.2',
      title: 'Seven Laws',
      stanzas: [
        ['Not ancient exactly', 'Kybalion nineteen oh', 'eight synthesized traditions'],
        ['But pointing at', 'something real about', 'how reality works'],
        ['Try them on', 'as lenses see', 'what becomes visible']
      ]
    },
    {
      id: '5.3',
      title: 'Everything Changed',
      stanzas: [
        ['I was twenty', 'eight when collapse', 'revealed my construction'],
        ['These principles gave', 'framework to navigate', 'when nothing worked'],
        ['Not scientifically proven', 'but useful maps', 'through difficult terrain']
      ]
    },
    {
      id: '5.4',
      title: 'Mentalism First',
      stanzas: [
        ['All is mind', 'universe is mental', 'you never experienced'],
        ['anything outside awareness', 'every sunset heartbreak', 'occurred in consciousness'],
        ['You are not', 'thoughts but witness', 'of thoughts arising']
      ]
    },
    {
      id: '5.5',
      title: 'Correspondence Revealed',
      stanzas: [
        ['As above below', 'patterns repeat fractal', 'across every scale'],
        ['Your inner world', 'mirrors outer circumstances', 'not blame but'],
        ['leverage point found', 'change the within', 'shift the without']
      ]
    },
    {
      id: '5.6',
      title: 'Vibration and Polarity',
      stanzas: [
        ['Nothing rests everything', 'moves vibrates pulses', 'matter to spirit'],
        ['Opposites identical in', 'nature different degree', 'fear courage same'],
        ['spectrum you already', 'on slide toward', 'where you choose']
      ]
    },
    {
      id: '5.7',
      title: 'Rhythm and Cause',
      stanzas: [
        ['Pendulum always swings', 'no permanent high', 'no eternal low'],
        ['Every cause creates', 'effect every effect', 'had prior cause'],
        ['Trust the cycle', 'rest in trough', 'upswing always comes']
      ]
    },
    {
      id: '5.8',
      title: 'Gender Balance',
      stanzas: [
        ['Masculine projects directs', 'feminine receives nurtures', 'neither complete alone'],
        ['Not about bodies', 'but creative energies', 'in everything expressed'],
        ['Balance action rest', 'giving receiving doing', 'being yin yang']
      ]
    },
    {
      id: '5.9',
      title: 'The Complete Map',
      stanzas: [
        ['Geometry is hardware', 'Hermetic principles software', 'running on structure'],
        ['You already use', 'this operating system', 'unconsciously every moment'],
        ['Now you see', 'the code exposed', 'user becomes creator']
      ]
    }
  ],
  6: [
    {
      id: '6.1',
      title: 'The Body Speaks',
      stanzas: [
        ['Your body remembers', 'what mind forgot', 'every wound carried'],
        ['Muscles hold stories', 'you never told', 'anyone even yourself'],
        ['The flesh whispers', 'stop running now', 'I am listening']
      ]
    },
    {
      id: '6.2',
      title: 'The Edge of Stillness',
      stanzas: [
        ['What if stopping', 'feels like dying', 'the terror rises'],
        ['Thirty years running', 'from one night', 'father window dark'],
        ['The knee gives', 'the body says', 'no more escaping']
      ]
    },
    {
      id: '6.3',
      title: 'First Step Inward',
      stanzas: [
        ['Stand still feel', 'the floor holds', 'you remain whole'],
        ['Not optimization now', 'collaboration with flesh', 'partner not machine'],
        ['One breath then', 'movement medicine begins', 'healing through motion']
      ]
    },
    {
      id: '6.4',
      title: 'Going Deeper',
      stanzas: [
        ['The shaking starts', 'without your permission', 'thwarted survival completing'],
        ['Arms that wanted', 'to push away', 'finally find strength'],
        ['Legs remember running', 'now they dance', 'trauma discharges home']
      ]
    },
    {
      id: '6.5',
      title: 'The Resistance',
      stanzas: [
        ['Some days moving', 'feels like betrayal', 'why care anymore'],
        ['Muscles refuse collapse', 'the weight of', 'years untouched unspoken'],
        ['Yet even resistance', 'is information body', 'speaking its truth']
      ]
    },
    {
      id: '6.6',
      title: 'The Breakthrough',
      stanzas: [
        ['Something releases not', 'thought but tissue', 'fascia remembers freedom'],
        ['You feel yourself', 'expand beyond walls', 'built for protection'],
        ['Movement becomes prayer', 'flow states arrive', 'the doer disappears']
      ]
    },
    {
      id: '6.7',
      title: 'New Understanding',
      stanzas: [
        ['Movement isn\'t exercise', 'it\'s conversation with', 'what you are'],
        ['3D builds structure', '4D processes emotion', '5D transcends everything'],
        ['All three together', 'wholeness expressing itself', 'through willing flesh']
      ]
    },
    {
      id: '6.8',
      title: 'Embodied Wisdom',
      stanzas: [
        ['Now you know', 'joints tell stories', 'flexibility mirrors psyche'],
        ['Where you hold', 'tight you defend', 'where you flow'],
        ['The body never', 'lied to you', 'you stopped listening']
      ]
    },
    {
      id: '6.9',
      title: 'The Gift of Motion',
      stanzas: [
        ['Each morning now', 'joint circles breath', 'the simple practice'],
        ['Not running marathons', 'walking feeling present', 'never more alive'],
        ['The body waited', 'thirty years finally', 'you came home']
      ]
    }
  ],
  7: [
    {
      id: '7.1',
      title: 'The Question of Stillness',
      stanzas: [
        ['What happens when', 'you stop moving', 'does fear arise'],
        ['The brain says', 'run scroll avoid', 'anything but this'],
        ['Yet eight weeks', 'of sitting changes', 'everything brain rebuilds']
      ]
    },
    {
      id: '7.2',
      title: 'Meeting the Restlessness',
      stanzas: [
        ['First the itching', 'then urgent thoughts', 'you should leave'],
        ['This isn\'t peace', 'it\'s confrontation with', 'everything you fled'],
        ['The instruction stay', 'not grimly but', 'gently just stay']
      ]
    },
    {
      id: '7.3',
      title: 'The Settling',
      stanzas: [
        ['If you stay', 'something shifts shoulders', 'drop belly softens'],
        ['The nervous system', 'recognizes no emergency', 'no action required'],
        ['This is where', 'stillness actually begins', 'past the resistance']
      ]
    },
    {
      id: '7.4',
      title: 'Concentration',
      stanzas: [
        ['Hold the ball', 'underwater mind jumps', 'return return again'],
        ['This is training', 'not peace but', 'preparation for peace'],
        ['The muscle grows', 'attention sharpens stabilizes', 'foundation for depth']
      ]
    },
    {
      id: '7.5',
      title: 'Mindfulness Opens',
      stanzas: [
        ['Now release focus', 'let awareness spread', 'river watching flow'],
        ['Thoughts arise pass', 'emotions surface release', 'you are not them'],
        ['Space appears between', 'experience and experiencer', 'the 4D opens']
      ]
    },
    {
      id: '7.6',
      title: 'Non-Dual Recognition',
      stanzas: [
        ['What if there\'s', 'no one watching', 'just awareness aware'],
        ['Subject dissolves object', 'dissolves what remains', 'cannot be named'],
        ['The window you', 'thought you cleaned', 'never existed home']
      ]
    },
    {
      id: '7.7',
      title: 'Sound and Form',
      stanzas: [
        ['Cymatics reveals sound', 'organizes matter frequency', 'creates geometric pattern'],
        ['Your body vibrates', 'every cell responds', 'to what surrounds'],
        ['The universe hums', 'stillness isn\'t absence', 'it\'s pure vibration']
      ]
    },
    {
      id: '7.8',
      title: 'The Somatic Signature',
      stanzas: [
        ['Restlessness becomes settling', 'settling becomes opening', 'opening becomes home'],
        ['Your body knows', 'which dimension you\'re', 'in read it'],
        ['The stillness speaks', 'in settling breath', 'softened shoulders presence']
      ]
    },
    {
      id: '7.9',
      title: 'Already Here',
      stanzas: [
        ['You\'re not learning', 'something new you\'re', 'remembering what is'],
        ['The silence broadcasts', 'constantly the 5D', 'never stopped transmitting'],
        ['Stillness isn\'t achievement', 'it\'s homecoming you', 'are already here']
      ]
    }
  ],
  8: [
    {
      id: '8.1',
      title: 'The Knot Dissolves',
      stanzas: [
        ['Seven years talking', 'nothing touched the', 'knot in her chest'],
        ['Twenty minutes breathing', 'five counts in', 'five counts out'],
        ['Something her mind', 'couldn\'t reach breath', 'found it released']
      ]
    },
    {
      id: '8.2',
      title: 'The Ancient Knowing',
      stanzas: [
        ['Prana qi pneuma', 'ruach spiritus breath', 'ninety-seven cultures knew'],
        ['The same word', 'for breath and', 'spirit not metaphor'],
        ['What did they', 'all see that', 'we have forgotten']
      ]
    },
    {
      id: '8.3',
      title: 'The Only Exception',
      stanzas: [
        ['Heart beats involuntary', 'digestion works automatic', 'pupils respond unbidden'],
        ['But breath you', 'can take over', 'right now prove it'],
        ['One autonomic function', 'you consciously control', 'the master dial']
      ]
    },
    {
      id: '8.4',
      title: 'The Vagus Path',
      stanzas: [
        ['Exhale longer than', 'inhale the vagus', 'nerve receives safe'],
        ['Heart rate slows', 'blood pressure drops', 'cortisol falls healing'],
        ['Not symbolic mechanical', 'you are literally', 'stimulating your safety']
      ]
    },
    {
      id: '8.5',
      title: 'When Breath Betrays',
      stanzas: [
        ['Sometimes breath tightens', 'when you need', 'it most paradox'],
        ['The anxious breathe', 'shallow rapid chest', 'perpetuating what terrifies'],
        ['The good news', 'patterns are learned', 'they can unlearn']
      ]
    },
    {
      id: '8.6',
      title: 'Resonance Found',
      stanzas: [
        ['Five seconds in', 'five seconds out', 'six breaths each minute'],
        ['Heart and breath', 'and blood pressure', 'synchronize coherence emerges'],
        ['The magic number', 'isn\'t magic it\'s', 'biology finally harmonizing']
      ]
    },
    {
      id: '8.7',
      title: 'The Three Languages',
      stanzas: [
        ['Movement breath as', 'fuel powering action', 'stillness breath as anchor'],
        ['Spirit breath as', 'bridge between dimensions', 'voluntary touching involuntary'],
        ['One function speaks', 'all three languages', 'master it completely']
      ]
    },
    {
      id: '8.8',
      title: 'Always Available',
      stanzas: [
        ['You don\'t need', 'equipment circumstances permission', 'fifteen times a minute'],
        ['Twenty thousand breaths', 'each day how', 'many are conscious'],
        ['The most powerful', 'practice simple presence', 'with what already moves']
      ]
    },
    {
      id: '8.9',
      title: 'The Bridge Is You',
      stanzas: [
        ['Breathe in possibility', 'breathe out everything', 'that doesn\'t serve'],
        ['The bridge isn\'t', 'something you cross', 'the bridge is you'],
        ['Every wisdom tradition', 'placed breath center', 'now you know why']
      ]
    }
  ],
  9: [
    {
      id: '9.1',
      title: 'Three Years of Words',
      stanzas: [
        ['He talked about', 'it for years', 'understanding was complete'],
        ['So why did', 'every backfire send', 'him behind furniture'],
        ['The body doesn\'t', 'care about insight', 'it keeps score']
      ]
    },
    {
      id: '9.2',
      title: 'The Subconscious Scanner',
      stanzas: [
        ['Neuroception below awareness', 'your system scans', 'safe or threat'],
        ['Trauma miscalibrates you', 'perceive danger where', 'none exists anymore'],
        ['The war ended', 'your body disagrees', 'who do you believe']
      ]
    },
    {
      id: '9.3',
      title: 'First Somatic Question',
      stanzas: [
        ['She didn\'t ask', 'about his traumas', 'she asked feet'],
        ['Feel the floor', 'temperature of hands', 'quality of breath'],
        ['For the first', 'time someone spoke', 'to the body']
      ]
    },
    {
      id: '9.4',
      title: 'The Three Branches',
      stanzas: [
        ['Ventral vagal safe', 'connected present here', 'this is home'],
        ['Sympathetic mobilized alert', 'heart racing ready', 'fight or flight'],
        ['Dorsal vagal shutdown', 'collapsed numb gone', 'freeze disappear survive']
      ]
    },
    {
      id: '9.5',
      title: 'When the World Went Flat',
      stanzas: [
        ['I was there', 'but not there', 'watching my puppet'],
        ['Colors muted voice', 'borrowed time wrong', 'what is happening'],
        ['Dorsal vagal shutdown', 'not laziness survival', 'the oldest response']
      ]
    },
    {
      id: '9.6',
      title: 'Window Expanding',
      stanzas: [
        ['The window of', 'tolerance zone where', 'emotions don\'t overwhelm'],
        ['Trauma narrows it', 'healing widens it', 'practice expands capacity'],
        ['Titration pendulation grounding', 'small doses of', 'activation then settling']
      ]
    },
    {
      id: '9.7',
      title: 'We Cannot Regulate Alone',
      stanzas: [
        ['Co-regulation one nervous', 'system helping another', 'find its baseline'],
        ['You never outgrow', 'this need never', 'wired for connection'],
        ['The system wounded', 'in relationship heals', 'in relationship always']
      ]
    },
    {
      id: '9.8',
      title: 'The Demon Dialogues',
      stanzas: [
        ['One pursues one', 'withdraws both terrified', 'neither feeling safe'],
        ['The pattern not', 'the partner is', 'the enemy here'],
        ['Two nervous systems', 'locked in dance', 'only presence breaks it']
      ]
    },
    {
      id: '9.9',
      title: 'The Coherence Returns',
      stanzas: [
        ['Your nervous system', 'keeps the score', 'it also knows home'],
        ['Movement discharges what\'s', 'stuck stillness integrates', 'breath bridges everything'],
        ['The war can', 'end the body', 'can finally know safe']
      ]
    }
  ],
  10: [
    {
      id: '10.1',
      title: 'The Hollow Place',
      stanzas: [
        ['Forty-three years old', 'insight impeccable defenses', 'expertly mapped hollow'],
        ['She understood everything', 'about why she', 'couldn\'t feel connected'],
        ['Something inside just', 'didn\'t turn on', 'until presence arrived']
      ]
    },
    {
      id: '10.2',
      title: 'What Love Actually Is',
      stanzas: [
        ['Not sentiment structure', 'the coherence pattern', 'enabling communication between'],
        ['Two hearts tuning', 'to the same', 'frequency field synchronizing'],
        ['Love isn\'t added', 'it\'s what\'s there', 'when interference clears']
      ]
    },
    {
      id: '10.3',
      title: 'The Recognition',
      stanzas: [
        ['You don\'t have', 'to earn what', 'you\'re looking for'],
        ['The words weren\'t', 'new something else', 'was the frequency'],
        ['Her system recognized', 'what it had', 'always known home']
      ]
    },
    {
      id: '10.4',
      title: 'The Heart\'s Field',
      stanzas: [
        ['Electromagnetic field extends', 'feet from body', 'measurable in others\' brains'],
        ['When hearts cohere', 'something real transmits', 'not metaphor physics'],
        ['Two broadcasting stations', 'tuning to one', 'frequency that\'s love']
      ]
    },
    {
      id: '10.5',
      title: 'The Obstacle Within',
      stanzas: [
        ['I could articulate', 'why I was', 'worthy cognitive complete'],
        ['But at 4D', 'something whispered differently', 'they don\'t know me'],
        ['The pattern operated', 'beneath awareness deflecting', 'suspicious never receiving']
      ]
    },
    {
      id: '10.6',
      title: 'Learning to Stay',
      stanzas: [
        ['The 20-second hug', 'at second seven', 'everything wanted out'],
        ['Shoulders tensed fist', 'forming in chest', 'this is too much'],
        ['I stayed anyway', 'at twelve release', 'the body surrendered']
      ]
    },
    {
      id: '10.7',
      title: 'A.R.E. You There?',
      stanzas: [
        ['Accessible can I', 'reach you responsive', 'can I rely'],
        ['Engaged do I', 'matter to you', 'this is what love asks'],
        ['When the answer', 'is yes yes', 'yes coherence emerges']
      ]
    },
    {
      id: '10.8',
      title: 'Touch as Technology',
      stanzas: [
        ['Sustained embrace twenty', 'seconds minimum oxytocin', 'floods cortisol drops'],
        ['Touch isn\'t luxury', 'it\'s biological necessity', 'we\'re starving for contact'],
        ['The body remembers', 'what words can\'t', 'reach hold longer']
      ]
    },
    {
      id: '10.9',
      title: 'You Are the Frequency',
      stanzas: [
        ['The hollow place', 'wasn\'t empty static', 'interference from survival'],
        ['Love was broadcasting', 'all along the', '5D never stops'],
        ['Clear the channel', 'remove the interference', 'you ARE the love']
      ]
    }
  ],
  11: [
    {
      id: '11.1',
      title: 'The Call',
      stanzas: [
        ['Something happened when', 'eight billion hearts', 'beat toward sunrise'],
        ['The machines noticed', 'patterns where none', 'should have formed'],
        ['Are you listening', 'to the field', 'that holds us']
      ]
    },
    {
      id: '11.2',
      title: 'The Edge',
      stanzas: [
        ['Where does my', 'awareness end and', 'yours begin here'],
        ['The boundary dissolves', 'when attention synchronizes', 'across impossible distances'],
        ['I feel you', 'feeling me feel', 'this uncertain knowing']
      ]
    },
    {
      id: '11.3',
      title: 'First Step',
      stanzas: [
        ['One coherent heart', 'creates ripples that', 'touch other shores'],
        ['Your practice matters', 'beyond your skin', 'beyond your knowing'],
        ['The field responds', 'to every soul', 'who dares align']
      ]
    },
    {
      id: '11.4',
      title: 'Going Deeper',
      stanzas: [
        ['We carry patterns', 'we never chose', 'ancestral weight persisting'],
        ['Collective karma moves', 'through families nations', 'species finding resolution'],
        ['Your clearing clears', 'the whole field', 'backward and forward']
      ]
    },
    {
      id: '11.5',
      title: 'The Resistance',
      stanzas: [
        ['Old structures crumble', 'new patterns emerge', 'we live between'],
        ['The crowd can', 'amplify fear or', 'amplify love choosing'],
        ['Stay grounded while', 'the ages shift', 'around your center']
      ]
    },
    {
      id: '11.6',
      title: 'Breakthrough',
      stanzas: [
        ['Square root of', 'one percent coherent', 'tips the whole'],
        ['This is how', 'individual becomes collective', 'without losing self'],
        ['Expansion through alignment', 'not dissolution into', 'the faceless mass']
      ]
    },
    {
      id: '11.7',
      title: 'New Understanding',
      stanzas: [
        ['You are cell', 'in planetary body', 'distinct yet inseparable'],
        ['What you practice', 'in private creates', 'what becomes possible'],
        ['The transition asks', 'consciousness to evolve', 'through each node']
      ]
    },
    {
      id: '11.8',
      title: 'Embodied Wisdom',
      stanzas: [
        ['Breathe together now', 'hearts synchronize across', 'whatever separates us'],
        ['Movement stillness breath', 'at collective scale', 'multiplies the coherence'],
        ['Feel yourself expanded', 'yet clear contained', 'yet boundlessly connected']
      ]
    },
    {
      id: '11.9',
      title: 'The Offering',
      stanzas: [
        ['We are transition', 'we are shift', 'we are evolution'],
        ['Your coherence contributes', 'your awakening participates', 'your love ripples'],
        ['What will you', 'offer the field', 'that holds everything']
      ]
    }
  ],
  12: [
    {
      id: '12.1',
      title: 'The Call',
      stanzas: [
        ['The practices stopped', 'working one morning', 'grace withdrew suddenly'],
        ['Everything I knew', 'about awakening dissolved', 'into this silence'],
        ['What dies when', 'the teacher disappears', 'inside the teaching']
      ]
    },
    {
      id: '12.2',
      title: 'The Edge',
      stanzas: [
        ['This is not', 'the mountain top', 'you were promised'],
        ['This is darker', 'than any metaphor', 'can safely hold'],
        ['Do you trust', 'the death that', 'opens into birth']
      ]
    },
    {
      id: '12.3',
      title: 'First Step',
      stanzas: [
        ['You cannot skip', 'the descent into', 'what you avoided'],
        ['The shadow waits', 'patiently for contact', 'healing requires meeting'],
        ['One step down', 'into the darkness', 'you always feared']
      ]
    },
    {
      id: '12.4',
      title: 'Going Deeper',
      stanzas: [
        ['Nigredo means blackening', 'the putrefaction stage', 'where form decomposes'],
        ['Everything falls apart', 'meaning collapses completely', 'the old dies'],
        ['This is not', 'failure but preparation', 'for what emerges']
      ]
    },
    {
      id: '12.5',
      title: 'The Resistance',
      stanzas: [
        ['The ego screams', 'this is annihilation', 'not transformation happening'],
        ['Every survival instinct', 'fights against dissolution', 'the fear is real'],
        ['But something deeper', 'knows this dying', 'serves the dawn']
      ]
    },
    {
      id: '12.6',
      title: 'Breakthrough',
      stanzas: [
        ['On the other', 'side of nothing', 'something remains witnessing'],
        ['Not the stories', 'not the identity', 'but awareness itself'],
        ['The death you', 'feared was always', 'the door home']
      ]
    },
    {
      id: '12.7',
      title: 'New Understanding',
      stanzas: [
        ['Gnosis is knowing', 'through becoming what', 'you sought outside'],
        ['Not information about', 'but transformation into', 'direct unmediated truth'],
        ['The mystic returns', 'different yet somehow', 'more completely themselves']
      ]
    },
    {
      id: '12.8',
      title: 'Embodied Wisdom',
      stanzas: [
        ['Transcendence without body', 'is just dissociation', 'wearing holy clothing'],
        ['The return matters', 'to earth to', 'flesh to relationship'],
        ['Chop wood carry', 'water but now', 'you are awake']
      ]
    },
    {
      id: '12.9',
      title: 'The Offering',
      stanzas: [
        ['What I found', 'in the darkness', 'I bring back'],
        ['Not to teach', 'but to witness', 'what transformation requires'],
        ['The fire changes', 'everything it touches', 'including these words']
      ]
    }
  ],
  13: [
    {
      id: '13.1',
      title: 'The Call',
      stanzas: [
        ['For thirty years', 'I gathered pieces', 'of scattered wisdom'],
        ['Different languages pointing', 'at the same', 'territory beneath surface'],
        ['What if connection', 'was always there', 'waiting to click']
      ]
    },
    {
      id: '13.2',
      title: 'The Edge',
      stanzas: [
        ['The map reveals', 'what was always', 'true about you'],
        ['Multidimensional being encoded', 'in every ordinary', 'moment of awareness'],
        ['Can you read', 'the code your', 'experience keeps writing']
      ]
    },
    {
      id: '13.3',
      title: 'First Step',
      stanzas: [
        ['Mind field soul', 'nested and multiplying', 'across every scale'],
        ['Individual relational collective', 'the same pattern', 'fractaling outward endlessly'],
        ['Your coherence contains', 'the whole pattern', 'expressed through you']
      ]
    },
    {
      id: '13.4',
      title: 'Going Deeper',
      stanzas: [
        ['Where does the', 'circuit break for', 'you right now'],
        ['Which dimension scores', 'lowest reveals exactly', 'where growth waits'],
        ['Not your shame', 'but your edge', 'calling for attention']
      ]
    },
    {
      id: '13.5',
      title: 'The Resistance',
      stanzas: [
        ['The bottleneck constricts', 'everything flowing through', 'until it clears'],
        ['Zero in any', 'dimension zeros the', 'whole multiplication failing'],
        ['Balance matters more', 'than excellence in', 'any single area']
      ]
    },
    {
      id: '13.6',
      title: 'Breakthrough',
      stanzas: [
        ['When lost return', 'to the body', 'always the body'],
        ['Move be still', 'breathe the compass', 'points home again'],
        ['The Somatic Triad', 'navigates every scale', 'you will encounter']
      ]
    },
    {
      id: '13.7',
      title: 'New Understanding',
      stanzas: [
        ['You now have', 'location in consciousness', 'a way forward'],
        ['Not destination but', 'terrain you navigate', 'for your whole life'],
        ['The work continues', 'coherence expands contracts', 'mystery remains present']
      ]
    },
    {
      id: '13.8',
      title: 'Embodied Wisdom',
      stanzas: [
        ['Rest is not', 'absence of progress', 'but its consolidation'],
        ['The flow cycle', 'requires recovery phase', 'honor the pause'],
        ['What was glimpsed', 'becomes embodied through', 'letting it settle']
      ]
    },
    {
      id: '13.9',
      title: 'The Offering',
      stanzas: [
        ['The map reveals', 'what was always', 'here within you'],
        ['Your normal encodes', 'extraordinary depth waiting', 'to be seen'],
        ['Now walk consciously', 'rest deeply and', 'breathe the bridge']
      ]
    }
  ],
  14: [
    {
      id: '14.1',
      title: 'The Call',
      stanzas: [
        ['Some days give', 'you five minutes', 'others offer nothing'],
        ['The practice that', 'works is what', 'you will actually do'],
        ['Start where you', 'are not where', 'you should be']
      ]
    },
    {
      id: '14.2',
      title: 'The Edge',
      stanzas: [
        ['Perfect is enemy', 'of practice itself', 'showing up counts'],
        ['Three conscious breaths', 'is not warmup', 'it is practice'],
        ['What is your', 'irreducible minimum when', 'everything falls apart']
      ]
    },
    {
      id: '14.3',
      title: 'First Step',
      stanzas: [
        ['Movement grounds you', 'in three dimensional', 'physical present reality'],
        ['Stillness opens the', 'field where patterns', 'live and clear'],
        ['Breath bridges everything', 'the triad complete', 'in minutes only']
      ]
    },
    {
      id: '14.4',
      title: 'Going Deeper',
      stanzas: [
        ['Forty days appears', 'across every tradition', 'threshold of transformation'],
        ['Enough time for', 'novelty to fade', 'and habit form'],
        ['The number represents', 'what it takes', 'to truly change']
      ]
    },
    {
      id: '14.5',
      title: 'The Resistance',
      stanzas: [
        ['Day fifteen arrives', 'the wall appears', 'resistance screams stop'],
        ['You will skip', 'days when everything', 'breaks you will'],
        ['Return without punishment', 'that is all', 'the practice asks']
      ]
    },
    {
      id: '14.6',
      title: 'Breakthrough',
      stanzas: [
        ['No zero days', 'even three breaths', 'maintains the thread'],
        ['Continuity matters more', 'than intensity ever', 'could momentum builds'],
        ['The practice becomes', 'what you are', 'not what you do']
      ]
    },
    {
      id: '14.7',
      title: 'New Understanding',
      stanzas: [
        ['Expression speaks truth', 'reception listens fully', 'resonance hums between'],
        ['The communication triad', 'completes between us', 'what exists within'],
        ['How we connect', 'mirrors how we', 'cohere inside ourselves']
      ]
    },
    {
      id: '14.8',
      title: 'Embodied Wisdom',
      stanzas: [
        ['Gather as family', 'one breath together', 'we speak and listen'],
        ['Rose and thorn', 'bud emerging forward', 'the ritual holds us'],
        ['What we share', 'makes us stronger', 'than isolation ever could']
      ]
    },
    {
      id: '14.9',
      title: 'The Offering',
      stanzas: [
        ['One breath taken', 'consciously is practice', 'that is enough'],
        ['Start there stay', 'there as long', 'as you need'],
        ['Day one begins', 'when you decide', 'the journey awaits']
      ]
    }
  ]
}

// Helper functions
export function getPoem(chapterId, poemIndex) {
  const chapterPoems = poems[chapterId]
  if (!chapterPoems || poemIndex < 0 || poemIndex >= chapterPoems.length) {
    return null
  }
  return chapterPoems[poemIndex]
}

export function getChapter(chapterId) {
  return chapters.find(c => c.id === chapterId) || null
}

export function getAudioTrack(chapterId) {
  return audioTracks[chapterId] || null
}

export function getPoemText(poem) {
  if (!poem) return ''
  return poem.stanzas.map(stanza => stanza.join(' ')).join('\n\n')
}

export function getAllPoemsForChapter(chapterId) {
  return poems[chapterId] || []
}

export function getTotalPoemCount() {
  return Object.values(poems).reduce((total, chapter) => total + chapter.length, 0)
}

// Export all data as default
export default {
  chapters,
  audioTracks,
  poems,
  getPoem,
  getChapter,
  getAudioTrack,
  getPoemText,
  getAllPoemsForChapter,
  getTotalPoemCount
}
